import { MutationType, ShapeType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../base/base-mutation';
import { Rule } from '../base/mutation-rules';
import { DataType, SHAPE_DATA_TYPES } from '../../data';
import { updateGroupTransform } from '../../utils/transform-utils';
import { cloneDeep } from '../../utils/clone-deep';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { MaybeError } from '../base/base-mutation';
import type {
    IValidComputeGroupTransformMutationData,
    IValidTransformShapeMutationData,
} from '../base/valid-mutation-data';

export class ComputeGroupTransformMutation extends BaseMutation<
    PresentationDataModel,
    IValidComputeGroupTransformMutationData
> {
    public readonly type: MutationType = MutationType.ComputeGroupTransform;

    public apply(presentation: PresentationDataModel) {
        const { groupId } = this.data;
        const shape = presentation.store.models.get(groupId, DataType.GroupShape);
        assert(shape);

        // 空组合不计算
        if (shape.shapeList.length <= 0) {
            return;
        }

        const groupTransform = updateGroupTransform(
            shape.shapeList.map((shape) => shape.getFrame()),
            shape.store.data.spPr.xfrm && shape.getFrame(),
        );
        const { off, ext, chOff, chExt } = groupTransform;

        shape.store.patchData({
            spPr: {
                xfrm: {
                    off,
                    ext,
                    chOff,
                    chExt,
                },
            },
        });
    }

    public invert(presentation: PresentationDataModel): [IValidTransformShapeMutationData] {
        const { pageId, groupId, parentId } = this.data;

        const shape = presentation.store.models.get(groupId, ...SHAPE_DATA_TYPES);
        assert(shape);

        const xfrm = cloneDeep(shape.store.data.spPr?.xfrm);
        const { off, ext, chOff, chExt } = xfrm ?? {};
        const xfrmData = xfrm ? { xfrmDelta: { off, ext }, chXfrmDelta: { chOff, chExt } } : { clearXfrm: true };
        return [
            {
                type: MutationType.TransformShape,
                pageId,
                shapeId: groupId,
                parentId,
                shapeType: ShapeType.GroupShape,
                ...xfrmData,
            },
        ];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, groupId } = this.data;
        return Rule.shape.should.existInPage(presentation, groupId, pageId);
    }
}
